import React from 'react';
import dayjs from 'dayjs';
import styles from './HygienistJobSelectionModal.module.scss';

export interface HygienistOfferItem {
  id?: number;
  jobId: number;
  status: number; // 0 => Custom, 3 => Expired
  hourlyRate?: number;
  offeredStartTime?: string;
  offeredEndTime?: string;
  job?: { location?: { practiceName?: string } };
}

export interface AvailableShiftItem {
  id: number;
  hourlyRate?: number;
  proposedRate?: number;
  avgRate?: number;
  location?: {
    practiceName?: string;
    city?: string;
    province?: string;
  };
  shifts?: Array<{
    postedStart?: string;
    postedEnd?: string;
  }>;
}

interface Props {
  isOpen: boolean;
  offers: HygienistOfferItem[];
  dateKey: string;
  hasAvailable: boolean;
  availableCount: number;
  availableShifts: AvailableShiftItem[];
  canShowInstabook: boolean;
  /** T2-5797: pending office Auto-book request for this date */
  pendingAutobookJobId?: number | null;
  /** T2-5797: talent already has a booked shift on this date. When set, the
   *  modal renders a "View Booked Shift" CTA so the user can choose between
   *  viewing the shift and editing their Auto-book schedule (instead of the
   *  previous behaviour where a booked date silently short-circuited to the
   *  confirmed page and hid the schedule options). */
  bookedJobId?: number | null;
  onSelectOffer: (jobId: number) => void;
  onSelectAvailableJob: (jobId: number) => void;
  onViewMore: () => void;
  onInstaBook: () => void;
  onPendingAutobook?: () => void;
  onViewBookedShift?: () => void;
  onClose: () => void;
}

const getStatusIcon = (status: number): string => {
  // 0 => Custom, 3 => Expired
  if (Number(status) === 3) return 'calendar-expired.svg';
  return 'calendar-custom.svg';
};

const formatTime = (val?: string): string => {
  if (!val) return '';
  const d = dayjs(val);
  return d.isValid() ? d.format('h:mm a') : val;
};

const HygienistJobSelectionModal: React.FC<Props> = ({
  isOpen,
  offers,
  dateKey,
  hasAvailable,
  availableCount,
  availableShifts,
  canShowInstabook,
  pendingAutobookJobId,
  bookedJobId,
  onSelectOffer,
  onSelectAvailableJob,
  onViewMore,
  onInstaBook,
  onPendingAutobook,
  onViewBookedShift,
  onClose
}) => {
  if (!isOpen) return null;

  // Determine title and subtitle based on what's available
  const hasOffers = (offers || []).length > 0;

  // T2-5855: drop any available shift that the talent already has an offer
  // for on this date — otherwise the same shift would render twice (once in
  // the offer list, once in the available list).
  const offerJobIds = new Set<number>(
    (offers || [])
      .map((o) => Number(o?.jobId))
      .filter((n) => Number.isFinite(n)),
  );
  const dedupedAvailableShifts = (availableShifts || []).filter(
    (job) => !offerJobIds.has(Number(job?.id)),
  );
  // When offers exist, the effective available count is still useful for the
  // "View More" CTA. `availableCount` coming from the parent is already
  // de-duped against the talent's own offers (see useHygienistCalendarData),
  // so we just pick the larger of the two sources to be safe.
  const effectiveAvailableCount = Math.max(availableCount, dedupedAvailableShifts.length, 0);
  const hasDedupedAvailable = effectiveAvailableCount > 0;

  // T2-5855: when offers AND other available shifts coexist, show the offers
  // plus a single "View More Available Shifts" button (not the individual
  // available shift rows) — the rows were producing duplicates with the
  // offer list and confused talent.
  const showIndividualAvailable =
    !hasOffers && hasDedupedAvailable && effectiveAvailableCount < 4 && dedupedAvailableShifts.length > 0;
  const showViewMoreButton =
    (!hasOffers && effectiveAvailableCount >= 4) ||
    (hasOffers && hasDedupedAvailable);
  /** T2-5796: when Auto-book is on and only browsing available shifts, lead with actions not "pick a row" */
  const autobookBrowseMode = !hasOffers && hasAvailable && canShowInstabook;

  let title = 'Select an Option';
  let subtitle = 'What would you like to do?';

  if (bookedJobId && !hasOffers && !hasDedupedAvailable) {
    // T2-5797: booked shift + Auto-book schedule on same day — offer both
    // actions instead of silently opening the confirmed shift.
    title = 'Booked Shift';
    subtitle = 'What would you like to do?';
  } else if (bookedJobId) {
    title = 'Select an Option';
    subtitle = 'What would you like to do?';
  } else if (hasOffers) {
    title = 'Select Custom Offer';
    subtitle = 'Which Custom Offer would you like to view?';
  } else if (showIndividualAvailable) {
    title = 'Available Shifts';
    subtitle = autobookBrowseMode ? '' : 'Select a shift to view details';
  } else if (autobookBrowseMode && showViewMoreButton) {
    title = 'Available Shifts';
    subtitle = '';
  }

  return (
    <>
      <div className={styles.modalOverlay} onClick={onClose} />
      <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="hjsel-title">
        <div className={styles.modalContent}>
          <h2 id="hjsel-title" className={styles.modalTitle}>{title}</h2>
          <p className={styles.modalSubtitle}>{subtitle}</p>

          <div className={styles.offerButtonsContainer}>
            {/* T2-5797: when the talent has both a booked shift and their
                own Auto-book schedule on this date, surface "View Booked
                Shift" as the top-most CTA so they can still jump to the
                confirmed shift details. The "Set/edit auto-book schedule"
                button still renders below when canShowInstabook is true. */}
            {!!bookedJobId && onViewBookedShift && (
              <button type="button" className={styles.instaButton} onClick={onViewBookedShift}>
                <img src="/img/calendar-booked.svg" alt="" width={22} height={22} />
                <div className={styles.instaText}><b>View Booked Shift</b></div>
              </button>
            )}
            {autobookBrowseMode && (
              <button type="button" className={styles.actionButton} onClick={onViewMore}>
                <span className={styles.greenSquare} aria-hidden="true" />
                <span>View Shifts</span>
              </button>
            )}
            {!!pendingAutobookJobId && onPendingAutobook && (
              <button type="button" className={styles.actionButton} onClick={onPendingAutobook}>
                <img src="/img/InstaBookBadge.png" alt="" />
                <span>Review pending Auto-book request</span>
              </button>
            )}
            {/* Custom Offers */}
            {(offers || []).map((off, idx) => {
              const practice = off?.job?.location?.practiceName || 'Offer';
              const time = `${formatTime(off?.offeredStartTime)} - ${formatTime(off?.offeredEndTime)}`;
              const rate = off?.hourlyRate ? ` @ $${off.hourlyRate}/hr` : '';
              return (
                <button
                  key={`${off.jobId}-${idx}`}
                  className={styles.offerButton}
                  onClick={() => onSelectOffer(off.jobId)}
                >
                  <div className={styles.offerButtonWrapper}>
                    <img className={styles.offerIcon} src={`/img/${getStatusIcon(off.status)}`} alt="status" />
                    <div className={styles.offerDetails}>
                      <div className={styles.offerTitle}><b>{practice}</b></div>
                      <div className={styles.offerTime}>{time}{rate}</div>
                    </div>
                  </div>
                </button>
              );
            })}

            {/* Individual Available Shifts (only when < 4 AND no offers — otherwise a single "View More" button is shown to avoid duplicating offer rows, T2-5855). Hide rows in autobook browse mode — use View Shifts instead (T2-5796) */}
            {showIndividualAvailable && !autobookBrowseMode && dedupedAvailableShifts.map((job, idx) => {
              const practice = job?.location?.practiceName || 'Available Shift';
              const city = job?.location?.city || '';
              const province = job?.location?.province || '';
              const locationStr = city && province ? `${city}, ${province}` : city || province || '';
              const shift = job?.shifts?.[0];
              const time = shift?.postedStart && shift?.postedEnd 
                ? `${shift.postedStart} - ${shift.postedEnd}` 
                : '';
              const rate = job?.proposedRate || job?.avgRate || job?.hourlyRate;
              const rateStr = rate ? `$${rate}/hr` : '';
              
              return (
                <button
                  key={`available-${job.id}-${idx}`}
                  className={styles.availableButton}
                  onClick={() => onSelectAvailableJob(job.id)}
                >
                  <div className={styles.offerButtonWrapper}>
                    <span className={styles.greenSquare} aria-hidden="true" />
                    <div className={styles.offerDetails}>
                      <div className={styles.offerTitle}><b>{practice}</b></div>
                      <div className={styles.offerTime}>
                        {locationStr && <span>{locationStr}</span>}
                        {time && <span> • {time}</span>}
                        {rateStr && <span> • {rateStr}</span>}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}

            {/* View More Available Shifts — rendered whenever there are offers + other available shifts, or when availableCount >= 4 (T2-5855). */}
            {showViewMoreButton && !autobookBrowseMode && (
              <button className={styles.actionButton} onClick={onViewMore}>
                <span className={styles.greenSquare} aria-hidden="true" />
                <span>
                  {hasOffers
                    ? 'View More Available Shifts >>'
                    : `View All ${effectiveAvailableCount} Available Shifts >>`}
                </span>
              </button>
            )}

            {canShowInstabook && (
              <button className={styles.instaButton} onClick={onInstaBook}>
                <img src="/img/InstaBookBadge.png" alt="" />
                <div className={styles.instaText}><b>Set/edit auto-book schedule</b></div>
              </button>
            )}

            <button className={`${styles.actionButton} ${styles.goBackButton}`} onClick={onClose}>
              Go Back
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default HygienistJobSelectionModal;


